import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Code2,
  Cpu,
  Download,
  FileText,
  FlaskConical,
  Globe,
  GraduationCap,
  HeartPulse,
  Library,
  Mail,
  MapPin,
  Menu,
  Monitor,
  Newspaper,
  Phone,
  Search,
  Sparkles,
  Users,
  Video,
  X,
  BrainCircuit,
  Cloud,
} from "lucide-react";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const API_SOURCE_URL = import.meta.env.VITE_API_SOURCE_URL;

const DEPARTMENT_ID = "5";
const PROGRAMME_ID = "1";

const theme = {
  primary: "#016C94",
  secondary: "#6BCCF2",
  light: "#E6FAFE",
  dark: "#014864",
};

const menuItems = [
  { label: "Overview", icon: BookOpen },
  { label: "Tuition Fees", icon: FileText },
  { label: "Financial Aid", icon: GraduationCap },
  { label: "Notice", icon: CalendarDays },
  { label: "News & Events", icon: Newspaper },
  { label: "Videos", icon: Video },
  { label: "Faculty Members", icon: Users },
  { label: "Computer Club", icon: Code2 },
  { label: "Research", icon: BrainCircuit },
  { label: "Facilities", icon: Building2 },
  { label: "Course Curriculum", icon: BookOpen },
  { label: "Course Offered", icon: GraduationCap },
  { label: "Contact Us", icon: Mail },
];

const researchAreas = [
  "Digital Image Processing",
  "Machine Learning",
  "Big Data & Data Mining",
  "Robotics",
  "IoT",
  "Materials Science",
];

const facilities = [
  { icon: FlaskConical, title: "Well Equipped Laboratories" },
  { icon: Building2, title: "Research Lab" },
  { icon: Library, title: "Rich Library" },
  { icon: Monitor, title: "Multimedia Classroom" },
  { icon: Users, title: "Counseling of Weak Students" },
  { icon: Globe, title: "Special Care for Foreign Students" },
  { icon: GraduationCap, title: "Adequate AC Classroom" },
  { icon: HeartPulse, title: "Health Care" },
  { icon: Cloud, title: "Online Academic Services" },
];

const feeRows = [
  { label: "Total Credits", value: "163" },
  { label: "Admission Fee (once, non-refundable)", value: "15,000" },
  { label: "Application Form Fee", value: "500" },
  { label: "Registration Fee (Per Semester)", value: "4,000" },
  { label: "Orientation Fee", value: "1,000" },
  { label: "Tuition Fee (Per Credit)", value: "3,400" },
  { label: "Development Fee", value: "1,500" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.07 },
  },
};

function formatDate(date) {
  if (!date) return "Date unavailable";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return "Date unavailable";

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getImageUrl(path) {
  if (!path) return null;

  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  return `${API_SOURCE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

function SectionHeading({ eyebrow, title, description }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="mb-8"
    >
      {eyebrow && (
        <span className="inline-flex items-center gap-2 rounded-full bg-[#E6FAFE] px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#016C94]">
          <Sparkles size={14} />
          {eyebrow}
        </span>
      )}

      <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
        {title}
      </h2>

      {description && (
        <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600 sm:text-base">
          {description}
        </p>
      )}
    </motion.div>
  );
}

function LoadingState({ text = "Loading information..." }) {
  return (
    <div className="flex min-h-48 flex-col items-center justify-center gap-4">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E6FAFE] border-t-[#016C94]" />
      <p className="text-sm font-medium text-slate-500">{text}</p>
    </div>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E6FAFE] text-[#016C94]">
        <FileText size={25} />
      </div>
      <h3 className="font-bold text-slate-800">{title}</h3>
      {description && (
        <p className="mt-2 text-sm text-slate-500">{description}</p>
      )}
    </div>
  );
}

function ErrorMessage({ error, onRetry }) {
  if (!error) return null;

  return (
    <div className="mb-5 flex flex-col gap-3 rounded-xl border border-red-100 bg-red-50 p-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-red-700">{error}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
        >
          Try again
        </button>
      )}
    </div>
  );
}

export default function DepartmentCSE({
  accent = theme.primary,
  pageTitle = "Department of Computer Science and Engineering | University of Creative Technology Chittagong",
}) {
  const [activeTab, setActiveTab] = useState("Overview");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [faculty, setFaculty] = useState([]);
  const [search, setSearch] = useState("");
  const [facultyLoading, setFacultyLoading] = useState(false);
  const [facultyError, setFacultyError] = useState("");

  const [notices, setNotices] = useState([]);
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [noticeError, setNoticeError] = useState("");

  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState("");

  const [courses, setCourses] = useState({});
  const [selectedSemester, setSelectedSemester] = useState("");
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [coursesError, setCoursesError] = useState("");

  const [offeredCourses, setOfferedCourses] = useState({});
  const [selectedOfferedSemester, setSelectedOfferedSemester] = useState("");
  const [offeredLoading, setOfferedLoading] = useState(false);
  const [offeredError, setOfferedError] = useState("");

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const createApi = () =>
    axios.create({
      baseURL: API_BASE_URL,
      headers: { "Content-Type": "application/json" },
    });

  const createPayload = (extra = {}) => ({
    userId: import.meta.env.VITE_API_USER,
    password: import.meta.env.VITE_API_PASS,
    department_id: DEPARTMENT_ID,
    ...extra,
  });

  const facultyPost = async () => {
    setFacultyLoading(true);
    setFacultyError("");

    try {
      const response = await createApi().post(
        "/teacher-list",
        createPayload({ designation_id: "0" })
      );

      setFaculty(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setFacultyError(err.message || "Unable to load faculty information.");
    } finally {
      setFacultyLoading(false);
    }
  };

  const noticePost = async () => {
    setNoticeLoading(true);
    setNoticeError("");

    try {
      const response = await createApi().post(
        "/recent-notice",
        createPayload()
      );

      setNotices(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setNoticeError(err.message || "Unable to load notices.");
    } finally {
      setNoticeLoading(false);
    }
  };

  const newsPost = async () => {
    setNewsLoading(true);
    setNewsError("");

    try {
      const response = await createApi().post(
        "/recent-news",
        createPayload()
      );

      setNews(Array.isArray(response.data) ? response.data.slice(0, 48) : []);
    } catch (err) {
      setNewsError(err.message || "Unable to load news.");
    } finally {
      setNewsLoading(false);
    }
  };

  const coursePost = async () => {
    setCoursesLoading(true);
    setCoursesError("");

    try {
      const response = await createApi().post(
        "/program-syllabus",
        createPayload({ programme_id: PROGRAMME_ID })
      );

      const data = response.data || {};

      setCourses(data);

      const semesters = Object.keys(data);

      if (semesters.length > 0) {
        setSelectedSemester((current) =>
          current && semesters.includes(current) ? current : semesters[0]
        );
      }
    } catch (err) {
      setCoursesError(err.message || "Unable to load course curriculum.");
    } finally {
      setCoursesLoading(false);
    }
  };

  const offerCourse = async () => {
    setOfferedLoading(true);
    setOfferedError("");

    try {
      const response = await createApi().post(
        "/course-offered",
        createPayload({ programme_id: PROGRAMME_ID })
      );

      const data = response.data || {};

      setOfferedCourses(data);

      const semesters = Object.keys(data);

      if (semesters.length > 0) {
        setSelectedOfferedSemester((current) =>
          current && semesters.includes(current) ? current : semesters[0]
        );
      }
    } catch (err) {
      setOfferedError(err.message || "Unable to load offered courses.");
    } finally {
      setOfferedLoading(false);
    }
  };

  useEffect(() => {
    facultyPost();
    noticePost();
    newsPost();
    coursePost();
    offerCourse();
    // Initial API loading only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredFaculty = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return faculty;

    return faculty.filter((item) =>
      [
        item.name,
        item.department,
        item.department_name,
        item.designation,
        item.designation_name,
        item.education,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    );
  }, [faculty, search]);

  const changeTab = (tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
  };

  const semesterKeys = Object.keys(courses || {});
  const offeredSemesterKeys = Object.keys(offeredCourses || {});

  const renderOverview = () => (
    <div className="space-y-8">
      <div className="rounded-2xl bg-gradient-to-br from-[#E6FAFE] to-white p-6 sm:p-8">
        <div className="mb-4 inline-flex rounded-xl bg-white p-3 text-[#016C94] shadow-sm">
          <BookOpen size={25} />
        </div>

        <h2 className="text-2xl font-extrabold text-[#016C94] sm:text-3xl">
          Computer Science and Engineering
        </h2>

        <p className="mt-4 leading-8 text-slate-700">
          The Department of Computer Science and Engineering (CSE) at
          University of Creative Technology, Chittagong (UCTC) is a place
          where brightest minds from all over the country and abroad assemble
          for a greater future. We want to build a strong alumni association
          to exchange academic and professional knowledge and experience among
          existing and former students. Our alumni serve in top-ranking
          universities as teachers and researchers and in leading companies as
          software engineers and IT specialists. We organize co-curricular and
          extracurricular activities such as programming contests, workshops,
          job fairs, debating clubs, seminars, sports tournaments, and indoor
          games. We welcome all who are ready to take challenges and be the
          leaders of tomorrow.
        </p>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <motion.div variants={fadeUp} className="rounded-2xl border border-slate-100 p-6">
          <h3 className="flex items-center gap-3 text-xl font-bold text-[#016C94]">
            <span className="rounded-lg bg-[#E6FAFE] p-2">
              <Globe size={20} />
            </span>
            Vision
          </h3>
          <p className="mt-4 leading-7 text-slate-600">
            To produce competent professionals who shall be leaders in
            technology, industry and research at the national and international
            level.
          </p>
        </motion.div>

        <motion.div variants={fadeUp} className="rounded-2xl border border-slate-100 p-6">
          <h3 className="flex items-center gap-3 text-xl font-bold text-[#016C94]">
            <span className="rounded-lg bg-[#E6FAFE] p-2">
              <GraduationCap size={20} />
            </span>
            Mission
          </h3>
          <p className="mt-4 leading-7 text-slate-600">
            The mission of the Department of Computer Science and Engineering
            (CSE) is to teach and prepare educated, skilled, and ethically
            sound computer scientists and engineers for leadership and
            professional careers and for advanced studies.
          </p>
        </motion.div>
      </div>

      <div>
        <h3 className="mb-4 text-xl font-bold text-slate-900">Objectives</h3>
        <div className="space-y-3">
          {[
            "Apply knowledge of mathematics, science, engineering fundamentals, and specialization to conceptualize models.",
            "Design and develop solutions for complex engineering problems considering societal and environmental factors.",
            "Conduct investigations and synthesize data to provide valid conclusions.",
            "Use modern tools effectively and understand their limitations.",
            "Function effectively as an individual and team member or leader.",
            "Communicate effectively in both technical and societal contexts.",
            "Demonstrate understanding of ethics and professional responsibilities.",
          ].map((item) => (
            <div key={item} className="flex items-start gap-3">
              <CheckCircle2 className="mt-1 shrink-0 text-[#016C94]" size={18} />
              <p className="leading-7 text-slate-600">{item}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 p-6">
        <h3 className="text-xl font-bold text-[#016C94]">Why Choose CSE?</h3>
        <p className="mt-4 leading-8 text-slate-600">
          The most important aspect of computer science is problem solving —
          an essential life skill. Students study software and hardware design
          to solve problems across business, science, and social domains. With
          analytical and critical thinking skills, they excel in fields like
          big data, cybersecurity, and AI. Computer Science is challenging but
          rewarding, giving that same joy as when one creates their first
          program.
        </p>
      </div>

      <div>
        <h3 className="mb-4 text-xl font-bold text-slate-900">Specialty</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            "Java and C++ as Object-Oriented Programming Languages.",
            "Comprehensive web programming using .NET Core and Python Django.",
            "Machine Learning program development.",
            "NodeJS and MongoDB for software with unstructured databases.",
            "EEE, Physics, and other supportive lab facilities.",
          ].map((item, index) => (
            <div
              key={item}
              className="flex gap-3 rounded-xl bg-slate-50 p-4 text-sm leading-6 text-slate-700"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#016C94] text-xs font-bold text-white">
                {index + 1}
              </span>
              {item}
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl bg-slate-50 p-6">
          <h3 className="text-xl font-bold text-[#016C94]">Spirituality</h3>
          <p className="mt-4 leading-7 text-slate-600">
            The Department provides an excellent academic environment, guided
            by top-tier faculty engaged in world-class research. Students are
            encouraged to pursue research and publications early in their
            academic careers, fostering innovation and scholarly excellence.
          </p>
        </div>

        <div className="rounded-2xl bg-slate-50 p-6">
          <h3 className="text-xl font-bold text-[#016C94]">Alumni</h3>
          <p className="mt-4 leading-7 text-slate-600">
            CSE UCTC is always grateful to its alumni for their guidance and
            support. Alumni are active in cultural events, seminars,
            programming contests, convocations, and more, maintaining strong
            ties with the department.
          </p>
        </div>
      </div>

      <div className="rounded-2xl bg-[#016C94] p-6 text-white sm:p-8">
        <h3 className="text-xl font-bold">Why Study Here?</h3>
        <p className="mt-4 leading-7 text-white/85">
          UCTC offers 14 majors, 15 minors, and 100+ specialization areas.
          Beyond technical skills, it fosters social responsibility and
          justice, preparing students for ethical leadership in the global
          workforce.
        </p>
      </div>
    </div>
  );

  const renderTuitionFees = () => (
    <div>
      <SectionHeading
        eyebrow="Tuition & Fees"
        title="Tuition Fees Structure"
        description="Review the listed fee components for the CSE program."
      />

      <div className="overflow-hidden rounded-2xl border border-slate-100">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#016C94] text-white">
              <tr>
                <th className="px-5 py-4 text-left font-semibold">Description</th>
                <th className="px-5 py-4 text-right font-semibold">Amount (৳)</th>
              </tr>
            </thead>

            <tbody>
              {feeRows.map((row, index) => (
                <motion.tr
                  key={row.label}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.04 }}
                  className="border-b border-slate-100 transition hover:bg-[#E6FAFE]/70"
                >
                  <td className="px-5 py-4 font-medium text-slate-700">
                    {row.label}
                  </td>
                  <td className="px-5 py-4 text-right font-semibold text-slate-800">
                    {row.value}
                  </td>
                </motion.tr>
              ))}
            </tbody>

            <tfoot className="bg-[#E6FAFE] font-bold text-[#014864]">
              <tr>
                <td className="px-5 py-4">Total Fee</td>
                <td className="px-5 py-4 text-right">6,04,200</td>
              </tr>
              <tr>
                <td className="px-5 py-4">After 50% Waiver</td>
                <td className="px-5 py-4 text-right">3,27,100</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="mt-8 rounded-2xl bg-gradient-to-r from-[#016C94] to-[#6BCCF2] p-6 text-white sm:p-8">
        <h3 className="text-xl font-bold sm:text-2xl">
          Affordable Quality Education
        </h3>
        <p className="mt-3 leading-7 text-white/90">
          UCTC offers one of the most affordable tuition structures while
          ensuring world-class academic excellence. We believe in empowering
          students with opportunities through financial waivers and
          merit-based scholarships.
        </p>
      </div>
    </div>
  );

  const renderFinancialAid = () => (
    <div>
      <SectionHeading
        eyebrow="Scholarships"
        title="Financial Aid & Scholarships"
        description="Financial support information and policy guidelines."
      />

      <div className="rounded-2xl bg-gradient-to-br from-[#E6FAFE] to-white p-6 sm:p-8">
        <h3 className="text-xl font-bold text-[#016C94]">
          Financial Aid Overview
        </h3>
        <p className="mt-4 leading-7 text-slate-600">
          UCTC emphasizes providing assistance in case of any interruption in
          continuing study as well as motivating students with different
          scholarships in the following segments:
        </p>

        <ul className="mt-5 space-y-3">
          {[
            "Poor and meritorious students",
            "Special waiver for siblings, offspring of freedom fighters, tribal students, and national-level players",
            "Waiver on excellent results in semesters",
            "Need-based scholarships",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-slate-700">
              <CheckCircle2 size={18} className="mt-1 shrink-0 text-[#016C94]" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-100 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-[#016C94]">
          Policies & Guidelines
        </h3>

        <ul className="mt-5 space-y-3">
          {[
            "Scholarship/financial aid will be awarded to students who meet the eligibility criteria*",
            "All applications for scholarship/tuition waiver must be submitted within the university-announced deadline",
            "Only one of the waivers will be awarded to a particular student",
            "All tuition fee waivers depend on fund availability",
            "Students applying for renewal must take a minimum 15 credits course load",
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 text-slate-600">
              <CheckCircle2 size={18} className="mt-1 shrink-0 text-[#016C94]" />
              <span className="leading-7">{item}</span>
            </li>
          ))}
        </ul>

        <p className="mt-5 text-xs italic text-slate-500">
          *The financial aid policy is subject to change as guided by
          exigencies.
        </p>
      </div>

      <div className="mt-6 rounded-2xl bg-[#016C94] p-6 text-white sm:p-8">
        <h3 className="text-xl font-bold">
          Empowering Dreams through Financial Support
        </h3>
        <p className="mt-3 leading-7 text-white/85">
          UCTC believes that financial limitations should never hinder a
          student’s dream of education. Our extensive financial aid programs
          ensure that every student, regardless of background, has access to
          world-class education and opportunities.
        </p>
      </div>
    </div>
  );

  const renderNotice = () => (
    <div>
      <SectionHeading
        eyebrow="Department Updates"
        title="Recent Notices"
        description="Stay informed about important department announcements."
      />

      <ErrorMessage error={noticeError} onRetry={noticePost} />

      {noticeLoading ? (
        <LoadingState text="Loading recent notices..." />
      ) : notices.length === 0 ? (
        <EmptyState
          title="No notices found"
          description="There are no recent notices available right now."
        />
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="grid gap-5 md:grid-cols-2 xl:grid-cols-3"
        >
          {notices.map((notice) => (
            <motion.article
              key={notice.id}
              variants={fadeUp}
              whileHover={{ y: -5 }}
              className="group rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition hover:shadow-lg"
            >
              <div className="mb-4 flex items-center justify-between">
                <span className="rounded-lg bg-[#E6FAFE] p-3 text-[#016C94]">
                  <FileText size={22} />
                </span>
                <span className="text-xs font-medium text-slate-500">
                  {formatDate(notice.date)}
                </span>
              </div>

              <h3 className="line-clamp-3 text-lg font-bold leading-7 text-slate-800">
                {notice.head_line}
              </h3>

              <a
                href={`/notice/${notice.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#016C94] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#014864]"
              >
                View Details
                <ArrowRight size={16} />
              </a>
            </motion.article>
          ))}
        </motion.div>
      )}
    </div>
  );

  const renderNews = () => (
    <div>
      <SectionHeading
        eyebrow="News & Events"
        title="Latest Department News"
        description="Explore recent news, activities, and events from the department."
      />

      <ErrorMessage error={newsError} onRetry={newsPost} />

      {newsLoading ? (
        <LoadingState text="Loading department news..." />
      ) : news.length === 0 ? (
        <EmptyState
          title="No news found"
          description="Department news and events will appear here."
        />
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3"
        >
          {news.map((item) => (
            <motion.article
              key={item.id}
              variants={fadeUp}
              whileHover={{ y: -5 }}
              className="group overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition hover:shadow-xl"
            >
              <div className="relative h-48 overflow-hidden bg-slate-100">
                {item.images_path ? (
                  <img
                    src={getImageUrl(item.images_path)}
                    alt={item.head_line || "Department news"}
                    loading="lazy"
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-[#016C94]">
                    <Newspaper size={45} />
                  </div>
                )}
              </div>

              <div className="p-5">
                <p className="mb-3 flex items-center gap-2 text-xs text-slate-500">
                  <CalendarDays size={14} />
                  {formatDate(item.date)}
                </p>

                <h3 className="line-clamp-2 min-h-14 text-lg font-bold leading-7 text-slate-800">
                  {item.head_line}
                </h3>

                <Link
                  to={`/news/${item.id}`}
                  className="mt-5 inline-flex items-center gap-2 font-semibold text-[#016C94] transition hover:gap-3"
                >
                  Read More
                  <ArrowRight size={16} />
                </Link>
              </div>
            </motion.article>
          ))}
        </motion.div>
      )}
    </div>
  );

  const renderVideos = () => (
    <div>
      <SectionHeading
        eyebrow="Media"
        title="Department Videos"
        description="Department-related videos, tutorials, and lectures."
      />

      <EmptyState
        title="Videos will be available here"
        description="Department-related videos, tutorials, and lectures can be added to this section."
      />
    </div>
  );

  const renderFaculty = () => (
    <div>
      <SectionHeading
        eyebrow="Our People"
        title="Faculty Members"
        description="Meet the academic team of the Department of Computer Science and Engineering."
      />

      <div className="mb-7 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search
            size={19}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, department, designation..."
            className="w-full rounded-xl border border-slate-200 bg-white py-3.5 pl-11 pr-4 text-sm outline-none transition focus:border-[#016C94] focus:ring-4 focus:ring-[#016C94]/10"
          />
        </div>

        <div className="rounded-xl bg-[#E6FAFE] px-4 py-3 text-sm font-semibold text-[#016C94]">
          {filteredFaculty.length} Faculty Members
        </div>
      </div>

      <ErrorMessage error={facultyError} onRetry={facultyPost} />

      {facultyLoading ? (
        <LoadingState text="Loading faculty members..." />
      ) : filteredFaculty.length === 0 ? (
        <EmptyState
          title="No faculty found"
          description="Try another search term."
        />
      ) : (
        <motion.div
          layout
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3"
        >
          {filteredFaculty.map((person, index) => (
            <motion.article
              layout
              variants={fadeUp}
              key={person.teacher_id || person.id || index}
              whileHover={{ y: -5 }}
              className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition hover:shadow-xl"
            >
              <div className="h-2 bg-gradient-to-r from-[#016C94] to-[#6BCCF2]" />

              <div className="p-6 text-center">
                <div className="mx-auto mb-4 h-28 w-28 overflow-hidden rounded-full border-4 border-[#E6FAFE] bg-slate-100 shadow-sm">
                  <img
                    src={
                      getImageUrl(
                        person.picture || "assets/images/profile-picture.png"
                      )
                    }
                    alt={person.name || "Faculty member"}
                    loading="lazy"
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.src =
                        "https://placehold.co/160x160?text=Faculty";
                    }}
                  />
                </div>

                <h3 className="text-lg font-bold text-slate-900">
                  {person.name}
                </h3>

                <p className="mt-1 font-semibold text-[#016C94]">
                  {person.designation_name || person.designation}
                </p>

                <p className="mt-1 text-sm text-slate-500">
                  {person.department_name}
                </p>

                <div className="mt-5 space-y-2 border-t border-slate-100 pt-4 text-left text-sm text-slate-600">
                  {person.education && (
                    <p>
                      <strong className="text-slate-800">Education:</strong>{" "}
                      {person.education}
                    </p>
                  )}

                  {person.mobile_number && (
                    <p className="break-words">
                      <strong className="text-slate-800">Mobile:</strong>{" "}
                      {person.mobile_number}
                    </p>
                  )}

                  {person.email && (
                    <p className="break-words">
                      <strong className="text-slate-800">Email:</strong>{" "}
                      {person.email}
                    </p>
                  )}
                </div>

                <a
                  href={`/profile/${person.teacher_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#016C94] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#014864]"
                >
                  View Profile
                  <ArrowRight size={16} />
                </a>
              </div>
            </motion.article>
          ))}
        </motion.div>
      )}
    </div>
  );

  const renderComputerClub = () => (
    <div>
      <SectionHeading
        eyebrow="Student Activities"
        title="Computer Club"
        description="Information about the Computer Club and its activities."
      />

      <EmptyState
        title="Computer Club information"
        description="Club activities and announcements can be added to this section."
      />
    </div>
  );

  const renderResearch = () => (
    <div>
      <SectionHeading
        eyebrow="Innovation & Discovery"
        title="Innovative Research at UCTC CSE"
        description="Unlike many other CSE departments, we measure our worth not by the traditional academic pursuit of existing knowledge, but by taking strategic steps toward creating new knowledge through cutting-edge research. Our research projects are deeply embedded within national and international collaborative activities. We continually share our discoveries and insights through conferences, workshops, and global academic networks."
      />

      <h3 className="mb-5 text-lg font-bold text-slate-900">
        Our Research Areas
      </h3>

      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-2 gap-4 sm:grid-cols-3"
      >
        {researchAreas.map((area, index) => (
          <motion.div
            key={area}
            variants={fadeUp}
            whileHover={{ y: -5, scale: 1.02 }}
            className="rounded-2xl border border-[#E6FAFE] bg-gradient-to-br from-white to-[#E6FAFE] p-5 shadow-sm"
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#016C94] text-white">
              {index + 1}
            </div>
            <h4 className="text-sm font-bold leading-6 text-[#014864] sm:text-base">
              {area}
            </h4>
          </motion.div>
        ))}
      </motion.div>

      <div className="mt-8 rounded-2xl bg-gradient-to-r from-[#016C94] to-[#6BCCF2] p-6 text-white sm:p-8">
        <h3 className="text-xl font-bold">
          Pioneering the Future of Technology
        </h3>
        <p className="mt-3 leading-7 text-white/90">
          Our department fosters a research culture that inspires curiosity,
          critical thinking, and technological excellence. We empower our
          students and faculty to explore new horizons, turning creative ideas
          into real-world innovations that shape the digital future.
        </p>
      </div>
    </div>
  );

  const renderFacilities = () => (
    <div>
      <SectionHeading
        eyebrow="Campus Resources"
        title="Our Facilities"
        description="Get involved and stay up to date with what's happening at the Department of Computer Science and Engineering. We ensure an inspiring learning environment equipped with modern resources and supportive infrastructure."
      />

      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
      >
        {facilities.map((facility) => {
          const Icon = facility.icon;

          return (
            <motion.div
              key={facility.title}
              variants={fadeUp}
              whileHover={{ y: -5 }}
              className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:shadow-lg"
            >
              <div className="mb-4 inline-flex rounded-2xl bg-[#E6FAFE] p-4 text-[#016C94]">
                <Icon size={28} />
              </div>
              <h3 className="font-bold leading-6 text-slate-800">
                {facility.title}
              </h3>
            </motion.div>
          );
        })}
      </motion.div>

      <div className="mt-8 rounded-2xl bg-[#016C94] p-6 text-white sm:p-8">
        <h3 className="text-xl font-bold">Modern Learning Environment</h3>
        <p className="mt-3 leading-7 text-white/90">
          At UCTC CSE, we believe that the right environment nurtures
          innovation. Our world-class facilities ensure every student
          experiences hands-on learning, comfort, and access to cutting-edge
          technology.
        </p>
      </div>
    </div>
  );

  const renderCourseTable = (rows, includePrerequisite = false) => (
    <div className="overflow-hidden rounded-xl border border-slate-100">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-[#E6FAFE] text-[#014864]">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left">Credit</th>
              {includePrerequisite && (
                <th className="px-4 py-3 text-left">Pre-req</th>
              )}
            </tr>
          </thead>

          <tbody>
            {rows.map((course, index) => (
              <motion.tr
                key={course.course_id || course.actual_code || index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.025 }}
                className="border-b border-slate-100 transition hover:bg-slate-50"
              >
                <td className="px-4 py-3 text-slate-500">{course.sl}</td>
                <td className="whitespace-nowrap px-4 py-3 font-mono font-semibold text-[#016C94]">
                  {course.actual_code}
                </td>
                <td className="min-w-56 px-4 py-3 font-medium text-slate-700">
                  {course.course_title}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {course.credit_hours}
                </td>
                {includePrerequisite && (
                  <td className="px-4 py-3 text-slate-600">
                    {course.pre_requisite || "—"}
                  </td>
                )}
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSemesterButtons = (
    semesters,
    selected,
    setSelected,
    loading
  ) => (
    <div className="mb-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-700">
          Choose a semester
        </p>
        {loading && (
          <span className="text-xs text-slate-500">Loading...</span>
        )}
      </div>

      {semesters.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {semesters.map((semester) => (
            <button
              key={semester}
              type="button"
              onClick={() => setSelected(semester)}
              className={`whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                selected === semester
                  ? "bg-[#016C94] text-white shadow-md shadow-[#016C94]/20"
                  : "bg-slate-100 text-slate-600 hover:bg-[#E6FAFE] hover:text-[#016C94]"
              }`}
            >
              {semester.replace("Semester Number: ", "Semester ")}
            </button>
          ))}
        </div>
      ) : (
        <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
          Semester information is not available.
        </p>
      )}
    </div>
  );

  const renderCurriculum = () => (
    <div>
      <SectionHeading
        eyebrow="Academic Program"
        title="Course Curriculum"
        description="Browse the CSE curriculum semester by semester."
      />

      <ErrorMessage error={coursesError} onRetry={coursePost} />

      {coursesLoading && semesterKeys.length === 0 ? (
        <LoadingState text="Loading course curriculum..." />
      ) : (
        <div className="rounded-2xl border border-slate-100 bg-white p-4 sm:p-6">
          {renderSemesterButtons(
            semesterKeys,
            selectedSemester,
            setSelectedSemester,
            coursesLoading
          )}

          {courses[selectedSemester]?.length ? (
            renderCourseTable(courses[selectedSemester], true)
          ) : (
            <EmptyState
              title="No courses available"
              description="There are no courses listed for this semester."
            />
          )}
        </div>
      )}
    </div>
  );

  const renderOfferedCourses = () => (
    <div>
      <SectionHeading
        eyebrow="Current Semester"
        title="Course Offered"
        description="Browse the courses offered in the last semester."
      />

      <ErrorMessage error={offeredError} onRetry={offerCourse} />

      {offeredLoading && offeredSemesterKeys.length === 0 ? (
        <LoadingState text="Loading offered courses..." />
      ) : (
        <div className="rounded-2xl border border-slate-100 bg-white p-4 sm:p-6">
          {renderSemesterButtons(
            offeredSemesterKeys,
            selectedOfferedSemester,
            setSelectedOfferedSemester,
            offeredLoading
          )}

          {offeredCourses[selectedOfferedSemester]?.length ? (
            renderCourseTable(offeredCourses[selectedOfferedSemester], false)
          ) : (
            <EmptyState
              title="No offered courses available"
              description="There are no offered courses listed for this semester."
            />
          )}
        </div>
      )}
    </div>
  );

  const renderContact = () => (
    <div>
      <SectionHeading
        eyebrow="Get in Touch"
        title="Contact the Department"
        description="Reach out to the Department of Computer Science and Engineering at UCTC."
      />

      <div className="grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl bg-gradient-to-br from-[#016C94] to-[#014864] p-6 text-white sm:p-8">
          <div className="mb-5 inline-flex rounded-xl bg-white/10 p-3">
            <MapPin size={25} />
          </div>
          <h3 className="text-xl font-bold">Visit Us</h3>
          <p className="mt-3 leading-7 text-white/85">
            Chandgaon Police Station, 1084, Shah Amanat Bridge Connecting
            Road, Chittagong 4212
          </p>
        </div>

        <div className="space-y-4">
          <a
            href="tel:01707507073"
            className="flex items-center gap-4 rounded-2xl border border-slate-100 p-5 transition hover:border-[#6BCCF2] hover:bg-[#E6FAFE]/50"
          >
            <span className="rounded-xl bg-[#E6FAFE] p-3 text-[#016C94]">
              <Phone size={22} />
            </span>
            <span>
              <span className="block text-xs text-slate-500">Phone</span>
              <span className="mt-1 block font-semibold text-slate-800">
                01707507073
              </span>
            </span>
          </a>

          <a
            href="mailto:cse@uctc.edu.bd"
            className="flex items-center gap-4 rounded-2xl border border-slate-100 p-5 transition hover:border-[#6BCCF2] hover:bg-[#E6FAFE]/50"
          >
            <span className="rounded-xl bg-[#E6FAFE] p-3 text-[#016C94]">
              <Mail size={22} />
            </span>
            <span>
              <span className="block text-xs text-slate-500">Email</span>
              <span className="mt-1 block break-all font-semibold text-slate-800">
                cse@uctc.edu.bd
              </span>
            </span>
          </a>

          <div className="flex items-center gap-4 rounded-2xl border border-slate-100 p-5">
            <span className="rounded-xl bg-[#E6FAFE] p-3 text-[#016C94]">
              <CalendarDays size={22} />
            </span>
            <span>
              <span className="block text-xs text-slate-500">Office Hours</span>
              <span className="mt-1 block font-semibold text-slate-800">
                Mon – Sat, 9:00 AM – 5:00 PM
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-slate-50 p-6">
        <h3 className="font-bold text-slate-900">Social Info</h3>
        <div className="mt-4 flex flex-wrap gap-3">
          {["Facebook", "LinkedIn", "YouTube"].map((social) => (
            <span
              key={social}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600"
            >
              {social}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "Overview":
        return renderOverview();
      case "Tuition Fees":
        return renderTuitionFees();
      case "Financial Aid":
        return renderFinancialAid();
      case "Notice":
        return renderNotice();
      case "News & Events":
        return renderNews();
      case "Videos":
        return renderVideos();
      case "Faculty Members":
        return renderFaculty();
      case "Computer Club":
        return renderComputerClub();
      case "Research":
        return renderResearch();
      case "Facilities":
        return renderFacilities();
      case "Course Curriculum":
        return renderCurriculum();
      case "Course Offered":
        return renderOfferedCourses();
      case "Contact Us":
        return renderContact();
      default:
        return renderOverview();
    }
  };

  return (
    <main className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-gradient-to-br from-[#014864] via-[#016C94] to-[#6BCCF2]">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-28 left-10 h-80 w-80 rounded-full bg-cyan-200/10 blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-14 sm:px-8 sm:py-20 lg:grid-cols-[1.2fr_.8fr]">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="relative z-10"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur">
              <Sparkles size={15} />
              Learn • Innovate • Create
            </span>

            <h1 className="mt-6 max-w-3xl text-3xl font-black leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Department of
              <span className="mt-2 block text-cyan-100">
                Computer Science
              </span>
              <span className="block">& Engineering</span>
            </h1>

            <p className="mt-5 max-w-2xl text-sm leading-7 text-white/85 sm:text-lg sm:leading-8">
              Welcome to the CSE Department of University of Creative
              Technology, Chittagong (UCTC), where innovation meets education.
              We nurture creative thinkers and problem solvers prepared to lead
              in technology, industry, and research.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/admission"
                className="group inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 font-bold text-[#016C94] shadow-lg transition hover:-translate-y-1 hover:bg-cyan-50"
              >
                Apply Online
                <ArrowRight
                  size={18}
                  className="transition group-hover:translate-x-1"
                />
              </Link>

              <button
                type="button"
                onClick={() => changeTab("Course Curriculum")}
                className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-5 py-3 font-semibold text-white transition hover:-translate-y-1 hover:bg-white/20"
              >
                Explore Curriculum
                <BookOpen size={18} />
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 25 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mx-auto w-full max-w-md"
          >
            <motion.div
              animate={{ y: [0, -9, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="rounded-[2rem] border border-white/20 bg-white/10 p-5 shadow-2xl backdrop-blur-xl sm:p-7"
            >
              <div className="flex items-center justify-between border-b border-white/15 pb-5">
                <div>
                  <p className="text-sm text-white/70">CSE Department</p>
                  <h2 className="mt-1 text-xl font-bold text-white">
                    Build the Future
                  </h2>
                </div>
                <div className="rounded-2xl bg-white/10 p-3 text-cyan-100">
                  <Code2 size={28} />
                </div>
              </div>

              <div className="my-6 rounded-2xl bg-slate-950/40 p-5 font-mono text-xs leading-7 text-cyan-100 sm:text-sm">
                <p>
                  <span className="text-pink-300">const</span> future ={" "}
                  <span className="text-yellow-200">"technology"</span>;
                </p>
                <p>
                  <span className="text-pink-300">function</span>{" "}
                  <span className="text-sky-200">innovate</span>() {"{"}
                </p>
                <p className="pl-4">
                  <span className="text-pink-300">return</span>{" "}
                  <span className="text-emerald-200">createSolutions</span>();
                </p>
                <p>{"}"}</p>
                <p className="mt-3 text-emerald-300">
                  // Your ideas can change the world.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Code2, label: "Coding" },
                  { icon: BrainCircuit, label: "AI & Data" },
                  { icon: Cpu, label: "Innovation" },
                ].map((item) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={item.label}
                      className="rounded-xl border border-white/10 bg-white/5 px-2 py-4 text-center text-white"
                    >
                      <Icon className="mx-auto mb-2 text-cyan-100" size={22} />
                      <span className="text-xs font-semibold">
                        {item.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Sticky Tabs */}
      <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex items-center gap-3 py-3">
            <div className="hidden shrink-0 items-center gap-2 sm:flex">
              <div className="rounded-lg bg-[#E6FAFE] p-2 text-[#016C94]">
                <GraduationCap size={20} />
              </div>
              <span className="text-sm font-bold text-slate-800">CSE</span>
            </div>

            <div className="min-w-0 flex-1">
				{/* Desktop tab menu */}
				<div className="hidden w-full lg:block">
				  <div className="grid grid-cols-5 gap-2 xl:grid-cols-7">
					{menuItems.map((tab) => {
					  const Icon = tab.icon;
					  const isActive = activeTab === tab.label;

					  return (
						<button
						  key={tab.label}
						  type="button"
						  onClick={() => {
							setActiveTab(tab.label);
							setMobileMenuOpen(false);
						  }}
						  className={`relative z-10 flex min-h-[44px] items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-all duration-300 ${
							isActive
							  ? "border-green-700 bg-green-700 text-white shadow-lg shadow-green-700/20"
							  : "border-slate-200 bg-white text-slate-600 hover:border-green-200 hover:bg-green-50 hover:text-green-700"
						  }`}
						>
						  {Icon && <Icon size={17} className="shrink-0" />}
						  <span className="text-center leading-tight">
							{tab.label}
						  </span>
						</button>
					  );
					})}
				  </div>
				</div>
              

              {/* Mobile tab selector */}
              <div className="flex items-center gap-2 lg:hidden">
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen((value) => !value)}
                  className="flex min-w-0 flex-1 items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
                >
                  <span className="flex items-center gap-2 truncate">
                    {React.createElement(
                      menuItems.find((item) => item.label === activeTab)?.icon ||
                        BookOpen,
                      { size: 17, className: "text-[#016C94]" }
                    )}
                    {activeTab}
                  </span>
                  {mobileMenuOpen ? (
                    <X size={17} />
                  ) : (
                    <ChevronDown size={17} />
                  )}
                </button>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden lg:hidden"
              >
                <div className="grid grid-cols-2 gap-2 pb-4 sm:grid-cols-3">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.label;

                    return (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => changeTab(item.label)}
                        className={`flex items-center gap-2 rounded-xl px-3 py-3 text-left text-xs font-semibold transition sm:text-sm ${
                          isActive
                            ? "bg-[#016C94] text-white"
                            : "bg-slate-50 text-slate-600 hover:bg-[#E6FAFE] hover:text-[#016C94]"
                        }`}
                      >
                        <Icon size={16} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Tab Content */}
      <section className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#016C94]">
              Department of CSE
            </p>
            <h2 className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">
              {activeTab}
            </h2>
          </div>

          <div className="hidden items-center gap-2 text-xs text-slate-400 sm:flex">
            <span>UCTC</span>
            <ChevronRight size={14} />
            <span className="font-semibold text-[#016C94]">{activeTab}</span>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="min-h-[60vh] rounded-2xl border border-slate-100 bg-white p-5 shadow-sm sm:p-8 lg:p-10"
          >
            {renderTabContent()}
          </motion.div>
        </AnimatePresence>

        {/* Bottom tab navigation */}
        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              const index = menuItems.findIndex(
                (item) => item.label === activeTab
              );
              const previous =
                menuItems[(index - 1 + menuItems.length) % menuItems.length];
              changeTab(previous.label);
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 transition hover:border-[#016C94] hover:text-[#016C94]"
          >
            <ChevronLeft size={17} />
            Previous
          </button>

          <span className="text-xs text-slate-400">
            {menuItems.findIndex((item) => item.label === activeTab) + 1} of{" "}
            {menuItems.length}
          </span>

          <button
            type="button"
            onClick={() => {
              const index = menuItems.findIndex(
                (item) => item.label === activeTab
              );
              const next = menuItems[(index + 1) % menuItems.length];
              changeTab(next.label);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-[#016C94] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#014864]"
          >
            Next
            <ChevronRight size={17} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-5 py-7 sm:px-8 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="font-bold text-slate-800">
              Department of Computer Science and Engineering
            </p>
            <p className="mt-1 text-sm text-slate-500">
              University of Creative Technology, Chittagong
            </p>
          </div>

          <a
            href="mailto:cse@uctc.edu.bd"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#016C94] transition hover:text-[#014864]"
          >
            <Mail size={17} />
            cse@uctc.edu.bd
          </a>
        </div>
      </footer>
    </main>
  );
}
